import { useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faHandPointer } from '@fortawesome/free-solid-svg-icons';
import { useReadAloudPaging } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/ReadAloud';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { BookType } from '@monorepo/shared/src/graph/schema-generated-types-educator';
import PageNavigation from '@monorepo/shared/src/components/common/PageNavigation';
import useBookStore from '@monorepo/shared/src/graph/state/query-hooks/use-book-store';
import { AppRoutes } from 'types';

const t = (key: string, options?) => translate('letterBooks', key, options);

type Props = {
  letter?: string;
  onPlayFindItClick: (letter: string) => void;
  onReadAloudClick: () => void;
};

type StylesProps = {
  letter?: string;
};

const LetterBookFooter: React.FC<Props> = ({ letter, onPlayFindItClick, onReadAloudClick }) => {
  const [{ book, isLoading, error }] = useBookStore({
    bookType: letter ? BookType.LETTER_BOOK : BookType.CLASSROOM_BOOK,
    letter,
  });

  const [{ activePages }, { goToPage, setTotalPages }] = useReadAloudPaging();
  const classes = useStyles({ letter });

  const isLetterTeacherModule = location.pathname.includes(AppRoutes.LETTER_TEACHING);

  const handleOnNavToPage = (pageNumber: number, isLastPage: boolean) =>
    goToPage(pageNumber, isLastPage);

  useEffect(() => {
    if (book && book.totalPages) {
      setTotalPages(book.totalPages);
    }
  }, [book]);

  return (
    <>
      <Box display="flex" className={classes.box} justifyContent="center">
        <Button
          className={classes.button}
          color="primary"
          onClick={onReadAloudClick}
          variant="contained"
        >
          <FontAwesomeIcon icon={faMicrophone} size="lg" />
          <Typography className={classes.buttonText}>{t('readAloud')}</Typography>
        </Button>
        {letter && isLetterTeacherModule && (
          <Button
            className={classes.button}
            color="primary"
            onClick={() => onPlayFindItClick(letter)}
            variant="outlined"
          >
            <FontAwesomeIcon icon={faHandPointer} size="lg" />
            <Typography className={classes.buttonText}>{t('playFindIt')}</Typography>
          </Button>
        )}
        {!isLoading && !error && book.type === BookType.CLASSROOM_BOOK && (
          <Box className={classes.pageNavigation}>
            <PageNavigation
              activePages={activePages}
              bookId={book.id}
              title={t('navigateToALetter')}
              subtitle={t('selectALetter')}
              hasPageNumberNavigation={false}
              onNavToPage={handleOnNavToPage}
              pageOffset={book.offset}
              totalPages={book.totalPages}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

const useStyles = makeStyles(() => ({
  box: {
    backgroundColor: '#fff',
    bottom: 0,
    position: 'absolute',
    width: '100%',
  },
  button: {
    margin: '10px 20px 10px 0px',
  },
  buttonText: {
    fontSize: 16,
    margin: '5px 10px 0',
    fontWeight: 'bold',
  },
  pageNavigation: {
    left: (props: StylesProps) => `calc(50% + ${props.letter ? '166px' : '88px'})`,
    position: 'absolute',
    top: remCalc(19),
  },
}));

export default LetterBookFooter;
